// =====================================================================
// src/screens/v2/WallTab.jsx
//
// Phase 2.5 / C10b — Merchant Wall tab.
//
// Two surfaces:
//   - A prominent "Compose post" button at the top (opens WallPostComposer)
//   - A scrollable list of the merchant's own posts (newest first)
//
// Subscribes to wallPosts.listWallPosts (real-time) so a new post shows
// immediately after publish + edits + deletes reflect instantly. Loading
// state shown until first snapshot arrives.
// =====================================================================

import React, { useEffect, useState } from 'react';
import { Plus, MessageCircleHeart } from 'lucide-react';
import { listWallPosts } from '../../lib/v2/wallPosts';
import WallPostCard from './WallPostCard';
import ImageViewerModal from '../../components/ImageViewerModal';

export default function WallTab({ businessId, businessName, ownerUid, onCompose, onEdit, onSetToast, onDeletePost, bizActive = true }) {
  const [posts, setPosts] = useState(null); // null = loading
  const [viewerUrl, setViewerUrl] = useState(null);

  useEffect(() => {
    if (!businessId) return;
    const unsub = listWallPosts({
      bizId: businessId,
      onSnap: (list) => setPosts(list),
      onError: (err) => {
        console.error('WallTab subscribe error:', err);
        setPosts([]);
        onSetToast?.(`Couldn't load posts: ${err.code || err.message || 'try again'}`);
      },
      limit: 50,
    });
    return unsub;
  }, [businessId, onSetToast]);

  // ---- Empty / loading states ----
  if (posts === null) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
        <div className="w-10 h-10 rounded-full border-2 animate-spin mb-3"
             style={{ borderColor: 'var(--border)', borderTopColor: 'var(--accent)' }} />
        <p className="text-[13px]" style={{ color: 'var(--ink-soft)' }}>Loading your wall…</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden" style={{ background: 'var(--bg)' }}>
      {/* Sticky top: Compose CTA — Session 6c2b — hidden when biz is
          inactive. A warm inline hint takes the same slot so the owner
          knows why the button is missing and what to do. */}
      <div className="shrink-0 px-4 pt-4 pb-3"
           style={{ borderBottom: '1px solid var(--border)' }}>
        {bizActive ? (
          <>
            <button onClick={onCompose}
                    className="w-full py-3 rounded-2xl flex items-center justify-center gap-2 text-[14px] font-medium active:scale-[0.99] transition-transform"
                    style={{ background: 'var(--ink)', color: '#fff' }}>
              <Plus size={16} /> Compose post
            </button>
            <p className="text-[11px] mt-2 text-center" style={{ color: 'var(--ink-soft)' }}>
              Share offers, new products, and updates with your customers
            </p>
          </>
        ) : (
          <div style={{
            padding: '10px 14px',
            background: '#FDF1ED',
            border: '1px solid #E8C8BB',
            color: '#8A3A20',
            borderRadius: 10,
            fontSize: 12,
            lineHeight: 1.5,
            textAlign: 'center',
          }}>
            Reactivate your Aangan Connect to post updates.
          </div>
        )}
      </div>

      {/* Posts list */}
      <div className="flex-1 overflow-y-auto px-4 pt-3 pb-6">
        {posts.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center px-4"
               style={{ minHeight: 300 }}>
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
                 style={{ background: 'var(--accent-soft)', border: '1px solid var(--border)' }}>
              <MessageCircleHeart size={26} style={{ color: 'var(--accent)' }} strokeWidth={1.5} />
            </div>
            <h2 className="mb-2"
                style={{ fontSize: '18px', fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500 }}>
              No posts yet
            </h2>
            <p className="text-[13px] max-w-[280px]" style={{ color: 'var(--ink-soft)' }}>
              {bizActive
                ? 'Compose your first post to share offers, announcements, and updates with customers in your Connect List.'
                : 'Reactivate your Aangan Connect to start posting.'}
            </p>
          </div>
        ) : (
          <>
            <div className="text-[11px] uppercase tracking-wider mb-2 mt-1 px-1"
                 style={{ color: 'var(--ink-soft)' }}>
              Your posts · {posts.length}
            </div>
            {posts.map(post => (
              <WallPostCard
                key={post.id}
                post={post}
                variant="merchant"
                onEdit={onEdit}
                onDelete={onDeletePost}
                onTapPhoto={(url) => setViewerUrl(url)}
                // Session 6c2b — WallPostCard hides its Edit/Delete menu
                // when disabled. Existing posts stay readable + tap-photo
                // still works, but modification is blocked.
                disabled={!bizActive}
              />
            ))}
          </>
        )}
      </div>

      {viewerUrl && (
        <ImageViewerModal src={viewerUrl} onClose={() => setViewerUrl(null)} />
      )}
    </div>
  );
}
